import { useState, useEffect } from 'react';
import { coins, certLookup, certificationServices } from '../../../utils/api';

export const useCoinForm = (initialData) => {
  const [formData, setFormData] = useState({
    user_coin_id: '',
    certification_service_id: '',
    certification_number: '',
    country: '',
    denomination: '',
    year: '',
    mint_mark: '',
    contact_id: '',
    variety: '',
    grade: '',
    metal: '',
    composition: '',
    provenance: '',
    storage_location: '',
    reference_number: '',
    assayer: '',
    cac_sticker: 'none',
    tags: [],
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: '',
    grading_cost: '',
    shipping_cost: '',
    status_name: 'Inventory',
    notes: '',
    coin_details: '',
    images: [],
    mintage: '',
    price_guide_value: '',
    list_price: '',
    population: '',
    pcgs_number: '',
    pop_higher: '',
    minor_variety: '',
    die_variety: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [focusedField, setFocusedField] = useState(null);

  // Format display values for numeric fields
  const getDisplayValue = (name, value) => {
    if (!value || focusedField === name) return value || '';
    
    const formatters = {
      price: (val) => `$${Number(val).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
      number: (val) => Number(val).toLocaleString(),
      mintage: (val) => {
        const match = val.match(/(\d+)/);
        if (!match) return val;
        const number = match[0];
        const index = val.indexOf(number);
        const formattedNumber = parseInt(number.replace(/,/g, '')).toLocaleString('en-US');
        return val.slice(0, index) + formattedNumber + val.slice(index + number.length);
      }
    };

    if (['price_guide_value', 'purchase_price', 'list_price', 'shipping_cost', 'grading_cost'].includes(name)) {
      return formatters.price(value);
    }
    if (['population', 'pop_higher'].includes(name)) {
      return formatters.number(value);
    }
    if (name === 'mintage') {
      return formatters.mintage(value);
    }
    return value;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const valueProcessors = {
      population: (val) => val.replace(/,/g, ''),
      price: (val) => {
        const numericValue = val.replace(/[^0-9.]/g, '');
        const parts = numericValue.split('.');
        return parts[0] + (parts.length > 1 ? '.' + parts[1] : '');
      },
      year: (val) => {
        const parts = val.trim().split(/\s+/);
        if (parts.length === 2 && /^\d{4}$/.test(parts[0])) {
          return `${parts[0]} ${parts[1]}`;
        }
        return val;
      }
    };

    let processedValue = value;
    if (['population', 'pop_higher'].includes(name)) {
      processedValue = valueProcessors.population(value);
    } else if (['price_guide_value', 'purchase_price', 'list_price', 'shipping_cost', 'grading_cost'].includes(name)) {
      processedValue = valueProcessors.price(value);
    } else if (name === 'year') {
      processedValue = valueProcessors.year(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // Initialize form
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch next user coin ID for new coins
        if (!initialData) {
          const data = await coins.getNextUserCoinId();
          setFormData(prev => ({
            ...prev,
            user_coin_id: data.nextUserCoinId
          }));
        }

        // Fetch certification services
        const servicesData = await certificationServices.getAll();
        setServices(servicesData);
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        setError('Failed to initialize form');
      }
    };

    fetchInitialData();
  }, [initialData]);

  // Set initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Validate form before submission
  const validateForm = () => {
    const errors = [];
    if (!formData.user_coin_id) errors.push('User coin ID is required');
    if (!formData.denomination) errors.push('Denomination is required');
    if (formData.year === undefined || formData.year === null) errors.push('Year is required');
    return errors;
  };

  // Get service-specific validation rules
  const getServiceRules = (serviceId) => {
    const service = services.find(s => s.id === parseInt(serviceId));
    if (!service) return null;

    const rules = {
      requiresGrade: service.requires_grade,
      validateLookup: () => {
        if (!formData.certification_number) {
          return 'Please enter a certification number';
        }
        if (service.requires_grade) {
          if (!formData.ngc_grade) {
            return `Please select a grade for ${service.name} lookup`;
          }
        }
        return null;
      }
    };

    if (service.requires_grade) {
      rules.formatGrade = (ngc_grade) => {
        if (ngc_grade === 'Details' || ngc_grade === 'Ancients') {
          return null;
        }
        return ngc_grade;
      };
    }

    return rules;
  };

  // Handle certification lookup
  const handleLookup = async () => {
    if (!formData.certification_service_id) {
      setError('Please select a certification service');
      return;
    }

    const service = getServiceRules(formData.certification_service_id);
    const validationError = service?.validateLookup();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLookupLoading(true);
    setError(null);

    try {
      const selectedService = services.find(s => s.id === parseInt(formData.certification_service_id));
      if (!selectedService) {
        throw new Error('Invalid certification service');
      }
      const certService = selectedService.name.toLowerCase();

      let grade = formData.ngc_grade;
      if (service?.formatGrade) {
        grade = service.formatGrade(grade);
      }

      const isPCGSNumber = certService === 'pcgs' && /^\d{1,6}$/.test(formData.certification_number);

      const response = await certLookup.lookup(
        certService,
        formData.certification_number,
        grade,
        isPCGSNumber
      );

      // Add population fields with special mapping
      response.current_population = response.population;
      response.current_pop_higher = response.pop_higher;

      // Map CAC sticker data to form format
      if (response.cac_sticker?.type) {
        response.cac_sticker = response.cac_sticker.type.toLowerCase();
      }

      // Update form data with response
      setFormData(prev => ({
        ...prev,
        ...response,
        // Preserve fields that shouldn't be overwritten
        certification_service_id: prev.certification_service_id,
        certification_number: prev.certification_number,
        images: prev.images
      }));

      return response;
    } catch (err) {
      console.error('Lookup error:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData?.error 
        ? `${errorData.error}${errorData.details ? `: ${errorData.details}` : ''}`
        : 'Failed to fetch coin details. Please try again or enter details manually.';
      setError(errorMessage);
    } finally {
      setLookupLoading(false);
    }
  };

  return {
    formData,
    setFormData,
    loading,
    setLoading,
    error,
    setError,
    lookupLoading,
    setLookupLoading,
    services,
    focusedField,
    setFocusedField,
    handleChange,
    getDisplayValue,
    validateForm,
    handleLookup
  };
};
